import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ProductFormDialog } from './ProductFormDialog';
import { evcpdProductsApi, type EvcpdProduct, type CreateProductInput } from '../../../api/evcpdProducts.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function ProductListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EvcpdProduct | null>(null);
  const [deleting, setDeleting] = useState<EvcpdProduct | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['evcpd-products', page, limit, search],
    queryFn: () => evcpdProductsApi.list({ page, limit, search: search || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['evcpd-products'] });

  const createMut = useMutation({
    mutationFn: (input: CreateProductInput) => evcpdProductsApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateProductInput }) => evcpdProductsApi.update(id, input),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => evcpdProductsApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<EvcpdProduct>[] = [
    { key: 'productCode', label: 'Code', width: 120 },
    { key: 'name', label: 'Product' },
    { key: 'category', label: 'Category', render: (r) => r.category.replace(/_/g, ' ') },
    { key: 'capacityKw', label: 'kW', render: (r) => (r.capacityKw ?? '—') },
    { key: 'unitPrice', label: 'Unit Price', align: 'right', render: (r) => inr(r.unitPrice) },
    { key: 'inventoryItemId', label: 'Stock-linked', render: (r) => (r.inventoryItemId ? `#${r.inventoryItemId}` : '—') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader
        title="EVCPD Products"
        subtitle="Dealer-orderable product catalog."
        action={hasPermission('evcpd_product.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New Product</Button>}
      />
      <Stack direction="row" mb={2}>
        <TextField size="small" placeholder="Search name / code / SKU" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('evcpd_product.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('evcpd_product.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />
      <ProductFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete product"
        message={`Soft-delete ${deleting?.name}?`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
